import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { getCappedVideoQualityId } from './videoCapping'
import 'bitmovin-player/bitmovinplayer-ui.css'
import './vertical-video.scss'
import { trackAutoplayNavigation, trackViewItem } from '../../lib/gtm'

const BITMOVIN_LICENSE_KEY = '34a49c54-19ea-45e1-a196-cda7e07caeb3'
const LIKE_DISLIKE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwU0BlApZbRng4l7xDVHaG5Fb9qQbe1sOEfmHnQETXOwXR9AeC39fkDtwNwprKJcBiJ1g/exec'

const MIN_BITRATE = 200 * 1000
const MAX_BITRATE = 400 * 1000

const MobileVerticalVideoPlayer = ({ videoId, playlist: initialPlaylist, combinedSlides: initialCombinedSlides, currentVideo, onChangeMeta, onClose, adTag, gtmConfig }) => {
    const [playlist, setPlaylist] = useState(initialPlaylist || [])
    const [combinedSlides, setCombinedSlides] = useState(initialCombinedSlides || [])
    const [isClient, setIsClient] = useState(false)
    const [swiperInstance, setSwiperInstance] = useState(null)
    const globalMuteState = useRef(true)
    const [, forceUpdate] = useState({})
    const [shareModalOpen, setShareModalOpen] = useState(false)
    const [shareUrl, setShareUrl] = useState('')
    const [isPlayingAd, setIsPlayingAd] = useState(false)
    const [currentSlideData, setCurrentSlideData] = useState(null)
    const [swiperLoaded, setSwiperLoaded] = useState(false)
    const [likeDislikeData, setLikeDislikeData] = useState({})
    const [likeDislikeLoaded, setLikeDislikeLoaded] = useState(false)
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
    const [socialPanelOpenId, setSocialPanelOpenId] = useState(null)

    // Single global player instance
    const playerRef = useRef(null)
    const swiperRef = useRef(null)
    const combinedSlidesRef = useRef([]) // Store slides in ref to avoid stale closures
    const backgroundControllerRef = useRef(null)
    const thumbnailPreloaderRef = useRef(null)
    const currentSlideIndexRef = useRef(0)
    const initialIndexRef = useRef(0)

    const qualityManagerRef = useRef({
      availableQualities: {},
      minLimit: { bitrate: MIN_BITRATE },
      maxLimit: { bitrate: MAX_BITRATE }
    });

    // Load external scripts
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = src
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
        })
    }

    // Load external scripts and initialize
    useEffect(() => {
        setIsClient(true)

        const loadScripts = async () => {
            try {
                // Load Swiper
                if (!window.Swiper) {
                    await loadScript('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js')
                }

                // Load Bitmovin Player
                // if (!window.bitmovin) {
                //     await loadScript('https://cdn.jsdelivr.net/npm/bitmovin-player@8/bitmovinplayer.min.js')
                //     await loadScript('https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.js')
                // }

                // Load transition controllers
                if (!window.BackgroundTransitionController) {
                    await loadScript('/js/BackgroundTransitionController.js')
                }
                if (!window.ThumbnailPreloader) {
                    await loadScript('/js/ThumbnailPreloader.js')
                }

                setSwiperLoaded(true)
            } catch (error) {
                console.error('Failed to load scripts:', error)
            }
        }

        loadScripts()
    }, [])

    // Sync ad audio
    const syncAdAudio = useCallback(() => {
        const player = playerRef.current
        if (!player) return

        try {
            if (!globalMuteState.current) {
                if (player.ads && player.ads.unmute) {
                    player.ads.unmute()
                }
                player.unmute && player.unmute()
            } else {
                if (player.ads && player.ads.mute) {
                    player.ads.mute()
                }
                player.mute && player.mute()
            }
        } catch (error) {
            console.error('Error syncing ad audio:', error)
        }
    }, [])

    // Create single Bitmovin player
    const createBitmovinPlayer = useCallback(async () => {
        if (playerRef.current) return

        qualityManagerRef.current = {
            availableQualities: {},
            minLimit: { bitrate: MIN_BITRATE },
            maxLimit: { bitrate: MAX_BITRATE }
        }

        const videoAdaptationHandler = (e) => {
            console.log('video adaptation handler: ', e)
            try {
                if (!qualityManagerRef.current.availableQualities || Object.keys(qualityManagerRef.current.availableQualities).length === 0) {
                    console.log('availableQualities not ready, using suggested quality');
                    return e.suggested;
                }

                const q = qualityManagerRef.current.availableQualities[e.suggested];

                if (q) {
                    if (q.bitrate < qualityManagerRef.current.minLimit.bitrate) {
                        console.log('bitrate too small, use the lowest quality with bitrate >= minLimit');
                        return qualityManagerRef.current.minLimit.quality ? qualityManagerRef.current.minLimit.quality.id : e.suggested;
                    } else if (q.bitrate > qualityManagerRef.current.maxLimit.bitrate) {
                        console.log('bitrate too high, use the highest quality with bitrate <= maxLimit');
                        return qualityManagerRef.current.maxLimit.quality ? qualityManagerRef.current.maxLimit.quality.id : e.suggested;
                    }
                }

                console.log('use the automatically selected quality');
                return e.suggested;
            } catch (error) {
                console.error('video adaptation handler error: ', error);
                return e.suggested; // Fallback to suggested quality
            }
        };

        const { Player } = await import('bitmovin-player')
        const { UIFactory } = await import('bitmovin-player-ui')

        const player = new Player(document.getElementById('bitmovin-player'), {
            key: BITMOVIN_LICENSE_KEY,
            playback: {
                muted: true,
                autoplay: false,
                preferredTech: [{ player: 'html5', streaming: 'hls' }],
            },
            advertising: {},
            ui: false,
            // adaptation: {
            //     startupBitrate: 300000,
            //     desktop: {
            //         onVideoAdaptation: videoAdaptationHandler,
            //     },
            //     mobile: {
            //         onVideoAdaptation: videoAdaptationHandler,
            //     }
            // },
            events: {
                sourceloaded: (e) => {
                    player.ads.schedule({
                        tag: {
                            type: 'vmap',
                            url: adTag,
                        },
                    })

                    // Check for immediate ad playback after scheduling
                    setTimeout(() => {
                        try {
                            const isCurrentlyPlayingAd = player.ads && player.ads.isLinearAdActive && player.ads.isLinearAdActive()
                            if (isCurrentlyPlayingAd && !isPlayingAd) {
                                setIsPlayingAd(true)
                                const playerDiv = document.querySelector('.video-container')
                                if (playerDiv) playerDiv.style.height = '100dvh'

                                const swiperEl = document.getElementById('swiper')
                                if (swiperEl) swiperEl.style.display = 'none'
                            }
                        } catch (error) {
                            console.error('Could not check ad state after source loaded:', error)
                        }
                    }, 200)
                },
            },
        })

        playerRef.current = player
        UIFactory.buildDefaultUI(player)

        // Handle ad events - attach immediately to catch early ad events
        player.on(player.exports.PlayerEvent.AdStarted, () => {
            setIsPlayingAd(true)
            const playerDiv = document.querySelector('.video-container')
            if (playerDiv) playerDiv.style.height = '100dvh'

            const swiperEl = document.getElementById('swiper')
            if (swiperEl) swiperEl.style.display = 'none'

            syncAdAudio()
        })

        player.on(player.exports.PlayerEvent.AdBreakStarted, () => {
            setIsPlayingAd(true)
            const playerDiv = document.querySelector('.video-container')
            if (playerDiv) playerDiv.style.height = '100dvh'

            const swiperEl = document.getElementById('swiper')
            if (swiperEl) swiperEl.style.display = 'none'

            syncAdAudio()
        })

        player.on(player.exports.PlayerEvent.AdBreakFinished, () => {
            setIsPlayingAd(false)
            const playerDiv = document.querySelector('.video-container')
            if (playerDiv) playerDiv.style.height = 'auto'

            const swiperEl = document.getElementById('swiper')
            if (swiperEl) swiperEl.style.display = ''

            // Restore video audio state based on global mute state
            if (!globalMuteState.current) {
                player.unmute && player.unmute()
            } else {
                player.mute && player.mute()
            }
        })

        player.on(player.exports.PlayerEvent.Play, () => {
            const activeIndex = currentSlideIndexRef.current
            const activeSlide = combinedSlides[activeIndex]

            if (!activeSlide || activeSlide.type !== 'video') return

            const videoData = { title: activeSlide.title, duration: activeSlide.duration || 0 }

            // console.log('[view_item] Mobile play fired', {
            //     title: activeSlide.title,
            //     activeIndex,
            //     listId: gtmConfig?.listId,
            //     listName: gtmConfig?.listName,
            //     category: gtmConfig?.category,
            //     category2: gtmConfig?.category2,
            // })

            trackViewItem(
                videoData,
                activeIndex,
                gtmConfig?.listId,
                gtmConfig?.listName,
                gtmConfig?.category,
                gtmConfig?.category2
            )
        })
    }, [isPlayingAd, syncAdAudio, combinedSlides, gtmConfig])

    // Get signed URL
    const getSignedUrl = useCallback(async (url) => {
        return url
    }, [])

    // Load video for current slide
    const loadVideoForSlide = useCallback(
        async (slideIndex) => {
            const slide = combinedSlides[slideIndex]
            const player = playerRef.current

            if (!slide || !player) return

            if (slide.type === 'ad') {
                // Hide player for ad slides
                const playerEl = document.getElementById('bitmovin-player')
                if (playerEl) playerEl.style.display = 'none'

                // Load ad
                setTimeout(() => {
                    if (window.loadAd) {
                        window.loadAd(slide.adId.toString())
                    }
                }, 50)
                return
            }

            const updateMinLimit = (limit) => {
                try {
                    limit = Number(limit);
                    if (!isNaN(limit) && qualityManagerRef.current.availableQualities && Object.keys(qualityManagerRef.current.availableQualities).length > 0) {
                        qualityManagerRef.current.minLimit = { bitrate: limit };
                        const availQ = Object.values(qualityManagerRef.current.availableQualities).filter(function (elem) {
                            return elem.bitrate >= qualityManagerRef.current.minLimit.bitrate;
                        });
                        if (availQ.length < 1) {
                            console.log('invalid limit, no quality left. Ignore limit');
                            return;
                        }
                        qualityManagerRef.current.minLimit.quality = availQ.reduce(function (a, b) {
                            if (a.bitrate <= b.bitrate) {
                                return a;
                            }
                            return b;
                        });

                        console.log('updated min limit with ' + limit);
                    }
                } catch (error) {
                    console.error('Error updating min limit:', error);
                }
            };

            const updateMaxLimit = (limit) => {
                try {
                  limit = Number(limit);
                  if (!isNaN(limit) && qualityManagerRef.current.availableQualities && Object.keys(qualityManagerRef.current.availableQualities).length > 0) {
                      qualityManagerRef.current.maxLimit = { bitrate: limit };
                      const availQ = Object.values(qualityManagerRef.current.availableQualities).filter(elem => elem.bitrate <= qualityManagerRef.current.maxLimit.bitrate)
                      if (availQ.length < 1) {
                          console.log('invalid limit, no quality left. Ignore limit');
                          return;
                      }
                      qualityManagerRef.current.maxLimit.quality = availQ.reduce((a, b) => {
                          if (a.bitrate > b.bitrate) {
                              return a;
                          }
                          return b;
                      });

                      console.log('updated max limit with ' + limit);
                  }
                } catch (error) {
                    console.error('Error updating max limit:', error);
                }
            };

            // Show player for video slides
            const playerEl = document.getElementById('bitmovin-player')
            if (playerEl) playerEl.style.display = ''

            if (backgroundControllerRef.current) {
                backgroundControllerRef.current?.beginAnimationTracking()
            }

            try {
                const signedUrl = await getSignedUrl(slide.hlsSource)
                // const signedUrl = slide.hlsSource

                await player.load({
                    title: slide.title,
                    hls: signedUrl,
                    poster: slide.poster,
                }).then(()=> {
                    const qualities = player.getAvailableVideoQualities()
                    console.log('Available quality: ', qualities)
                    let availableQualities = {}
                    qualities.forEach(q => {
                        availableQualities[q.id] = q
                    })

                    qualityManagerRef.current.availableQualities = availableQualities
                    updateMinLimit(MIN_BITRATE)
                    updateMaxLimit(MAX_BITRATE)

                    // player.on(window.bitmovin.player.PlayerEvent.VideoAdaptation, function (event) {
                    //     console.log('Current quality in mobile: ', event);
                    // });

                    // Apply global mute state to the player
                    if (globalMuteState.current) {
                        player.mute && player.mute()
                    } else {
                        player.unmute && player.unmute()
                    }

                    if (backgroundControllerRef.current) {
                        setTimeout(() => {
                            backgroundControllerRef.current?.endAnimationTracking()
                            const currentSlideEl = swiperRef.current?.slides[slideIndex]
                            if (currentSlideEl && slide.type === 'video') {
                                backgroundControllerRef.current.forceHideBackground(currentSlideEl)
                            }
                        }, 50)
                    }
                })

            } catch (error) {
                console.error('Error loading video:', error)
                if (backgroundControllerRef.current) {
                    backgroundControllerRef.current.endAnimationTracking()
                }
            }
        },
        [combinedSlides, getSignedUrl]
    )

    // Fetch like/dislike data from Google Apps Script
    const fetchLikeDislikeData = useCallback(async () => {
        try {
            const response = await fetch(`${LIKE_DISLIKE_SCRIPT_URL}?action=get`)
            if (response.ok) {
                const data = await response.json()
                const likeDislikeMap = {}

                data.forEach((item) => {
                    const articleId = item[0]
                    const likes = item[1] || 0
                    const dislikes = item[2] || 0

                    likeDislikeMap[articleId] = {
                        likes,
                        dislikes,
                        userAction: null,
                    }
                })
                setLikeDislikeData(likeDislikeMap)
                setLikeDislikeLoaded(true)
            } else {
                console.error('Failed to fetch like/dislike data:', response.status)
                setLikeDislikeLoaded(true)
            }
        } catch (error) {
            console.error('Error fetching like/dislike data:', error)
            setLikeDislikeLoaded(true)
        }
    }, [])

    // Update meta information
    const updateMeta = useCallback(
        (slideIndex) => {
            const slide = combinedSlides[slideIndex]
            if (!slide || slide.type === 'ad') return

            // const pathSegments = window.location.pathname.split('/').filter(Boolean)
            // if (pathSegments.length > 1) {
            //     pathSegments[pathSegments.length - 1] = slide.mediaid
            // } else {
            //     pathSegments.push(slide.mediaid)
            // }

            // const newUrl = `${window.location.origin}/${pathSegments.join('/')}`
            // window.history.replaceState({ videoId: slide.mediaid }, '', newUrl)

            const pageTitle = `${slide.title}`
            const pageDescription = slide.description || 'Tonton video menarik ini di BULETINTV3!'
            const imageUrl = slide.image

            // Update meta state - this will automatically update the Head component
            if (onChangeMeta) {
                onChangeMeta({
                    title: pageTitle,
                    description: pageDescription,
                    // url: newUrl,
                    image: imageUrl,
                    type: 'video.other',
                })
            }

            // Update document title
            document.title = pageTitle

            // Update current slide data for other components
            setCurrentSlideData({
                ...slide,
                description: pageDescription,
            })
        },
        [combinedSlides, onChangeMeta]
    )

    // Handle like/dislike
    const handleLikeDislike = useCallback(
        async (action, videoId) => {
            // Get current data
            const current = likeDislikeData[videoId] || { likes: 0, dislikes: 0, userAction: null }
            const alreadyInteracted = current.userAction !== null

            // Calculate new data
            const newData = { ...current }

            if (current.userAction === action) {
                return // Exit early, no changes
            } else if (!alreadyInteracted) {
                // First interaction

                if (action === 'like') {
                    newData.likes += 1
                } else {
                    newData.dislikes += 1
                }
                newData.userAction = action
            } else {
                // Changing from one to another (switching)

                if (action === 'like') {
                    // Switching from dislike to like
                    newData.likes += 1
                    newData.dislikes = Math.max(0, newData.dislikes - 1)
                } else {
                    // Switching from like to dislike
                    newData.dislikes += 1
                    newData.likes = Math.max(0, newData.likes - 1)
                }
                newData.userAction = action
            }

            // 1. UPDATE STATE IMMEDIATELY for better UX
            setLikeDislikeData((prev) => ({
                ...prev,
                [videoId]: newData,
            }))

            // 2. Update the current slide's UI immediately
            const currentSlide = document.querySelector(`[data-video-id="${videoId}"]`)
            if (currentSlide) {
                const likeCountElement = currentSlide.querySelector('.like-count')
                const dislikeCountElement = currentSlide.querySelector('.dislike-count')
                const likeBtnElement = currentSlide.querySelector('.like-btn')
                const dislikeBtnElement = currentSlide.querySelector('.dislike-btn')

                // Update counts immediately
                if (likeCountElement) likeCountElement.textContent = newData.likes
                if (dislikeCountElement) dislikeCountElement.textContent = newData.dislikes

                // Update visual state immediately
                if (likeBtnElement && dislikeBtnElement) {
                    // Always clear both buttons first
                    likeBtnElement.classList.remove('checked')
                    dislikeBtnElement.classList.remove('checked')

                    // Clear radio inputs
                    const likeRadio = likeBtnElement.querySelector('input[type="radio"]')
                    const dislikeRadio = dislikeBtnElement.querySelector('input[type="radio"]')
                    if (likeRadio) likeRadio.checked = false
                    if (dislikeRadio) dislikeRadio.checked = false

                    // Set the active state based on newData.userAction
                    if (newData.userAction === 'like') {
                        likeBtnElement.classList.add('checked')
                        if (likeRadio) likeRadio.checked = true
                    } else if (newData.userAction === 'dislike') {
                        dislikeBtnElement.classList.add('checked')
                        if (dislikeRadio) dislikeRadio.checked = true
                    }
                }
            }

            // 3. THEN send to API in the background (non-blocking)
            const sendToAPI = async () => {
                try {
                    const response = await fetch(LIKE_DISLIKE_SCRIPT_URL, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            action: action, // Always send the original action (like/dislike)
                            articleID: videoId,
                            alreadyInteracted: alreadyInteracted.toString(),
                        }),
                    })

                    if (!response.ok) {
                        console.error(`❌ Failed to send ${action} for video ${videoId}`)
                    }
                } catch (error) {
                    console.error(`💥 Error sending ${action} for video ${videoId}:`, error)
                }
            }

            // Send to API without blocking UI
            sendToAPI()
        },
        [likeDislikeData]
    )

    // Handle share
    const handleShare = useCallback(() => {
        setShareUrl(window.location.href)
        setShareModalOpen(true)
    }, [])

    // Setup like/dislike for active slide
    const setupActiveSliceLikeDislike = useCallback(
        (swiper) => {
            // Get fresh data from current state
            const currentLikeDislikeData = likeDislikeData

            if (!swiper || !likeDislikeLoaded) {
                return
            }

            const activeSlide = swiper.slides[swiper.activeIndex]
            if (!activeSlide) {
                return
            }

            // The activeSlide IS the swiper-slide, no need to query for it
            if (activeSlide.classList.contains('ad-slide')) {
                return
            }

            const videoID = activeSlide.getAttribute('data-video-id')
            if (!videoID) {
                return
            }

            // Update like/dislike counts from stored data
            const data = currentLikeDislikeData[videoID] || { likes: 0, dislikes: 0, userAction: null }

            const likeCountElement = activeSlide.querySelector('.like-count')
            const dislikeCountElement = activeSlide.querySelector('.dislike-count')
            const likeBtnElement = activeSlide.querySelector('.like-btn')
            const dislikeBtnElement = activeSlide.querySelector('.dislike-btn')

            if (likeCountElement) {
                likeCountElement.textContent = data.likes
            }
            if (dislikeCountElement) {
                dislikeCountElement.textContent = data.dislikes
            }

            // Update visual state based on user action
            if (likeBtnElement && dislikeBtnElement) {
                likeBtnElement.classList.remove('checked')
                dislikeBtnElement.classList.remove('checked')

                if (data.userAction === 'like') {
                    likeBtnElement.classList.add('checked')
                    const radioInput = likeBtnElement.querySelector('input[type="radio"]')
                    if (radioInput) radioInput.checked = true
                } else if (data.userAction === 'dislike') {
                    dislikeBtnElement.classList.add('checked')
                    const radioInput = dislikeBtnElement.querySelector('input[type="radio"]')
                    if (radioInput) radioInput.checked = true
                }
            }
        },
        [likeDislikeData, likeDislikeLoaded]
    )

    // Fetch like/dislike data when component mounts
    useEffect(() => {
        if (!isClient) return
        fetchLikeDislikeData()
    }, [isClient, fetchLikeDislikeData])

    // Initialize meta state with current URL when component mounts
    useEffect(() => {
        if (!isClient || !onChangeMeta) return

        // Initialize meta state with current URL
        onChangeMeta((prev) => ({
            ...prev,
            url: window.location.href,
        }))
    }, [isClient, onChangeMeta])

    // Initialize with server-side data when component mounts
    useEffect(() => {
        if (!isClient) return

        if (initialPlaylist && initialCombinedSlides) {
            setPlaylist(initialPlaylist)
            setCombinedSlides(initialCombinedSlides)
        }
    }, [isClient, initialPlaylist, initialCombinedSlides])

    // Initialize controllers and player when scripts are loaded
    useEffect(() => {
        if (!swiperLoaded || !isClient) return

        // Initialize controllers
        if (window.BackgroundTransitionController) {
            backgroundControllerRef.current = new window.BackgroundTransitionController()
        }
        if (window.ThumbnailPreloader) {
            thumbnailPreloaderRef.current = new window.ThumbnailPreloader()
        }

        // Create player
        createBitmovinPlayer().then(() => {
            // Load first video after player is ready
            const initialIndex = initialIndexRef.current || 0
            loadVideoForSlide(initialIndex)
        })
    }, [swiperLoaded, isClient, createBitmovinPlayer])

    // Update meta when currentVideo prop changes
    useEffect(() => {
        if (currentVideo && isClient && onChangeMeta) {
            const pageTitle = currentVideo.title
            const pageDescription = currentVideo.description || 'Tonton video menarik ini di BULETINTV3!'
            const imageUrl = currentVideo.image

            onChangeMeta({
                title: pageTitle,
                description: pageDescription,
                url: window.location.href,
                image: imageUrl,
                type: 'video.other',
            })

            // Update document title
            document.title = pageTitle
        }
    }, [currentVideo, isClient, onChangeMeta])

    // Setup like/dislike when data loads or slide changes
    useEffect(() => {
        if (!isClient || !likeDislikeLoaded || !swiperInstance) {
            return
        }

        setupActiveSliceLikeDislike(swiperInstance)
    }, [isClient, likeDislikeLoaded, swiperInstance, setupActiveSliceLikeDislike, likeDislikeData])

    // Update meta tags when current slide data changes
    useEffect(() => {
        if (!isClient || !currentSlideData || !onChangeMeta) return

        // Small delay to ensure DOM is ready
        setTimeout(() => {
            const pageTitle = currentSlideData.title
            const pageDescription = currentSlideData.description || 'Tonton video menarik ini di BULETINTV3!'
            const imageUrl = currentSlideData.image

            onChangeMeta({
                title: pageTitle,
                description: pageDescription,
                url: window.location.href,
                image: imageUrl,
                type: 'video.other',
            })
        }, 100)
    }, [isClient, currentSlideData, onChangeMeta])

    // Pause video when switching to ad slide
    const pauseVideo = useCallback((swiper) => {
        const currentIdx = swiper.activeIndex
        const prevIdx = swiper.previousIndex
        if (typeof currentIdx !== 'number' || typeof prevIdx !== 'number') return
        const currentSlide = swiper.slides[currentIdx]
        const prevSlide = swiper.slides[prevIdx]
        if (!currentSlide || !prevSlide) return
        if (currentSlide.classList.contains('ad-slide') && !prevSlide.classList.contains('ad-slide')) {
            const player = playerRef.current
            if (player) {
                player.pause && player.pause()
            }
        }
    }, [])

    // Track swipe for analytics
    const trackSwipe = useCallback(() => {
        if (!swiperRef.current) return

        const activeSlide = swiperRef.current.slides[swiperRef.current.activeIndex]
        const videoID = activeSlide.getAttribute('data-video-id')
        const videoTitle = activeSlide.getAttribute('data-title')
        const videoDuration = activeSlide.getAttribute('data-duration')
        const label = videoID + ' - ' + videoTitle
        const activeIdx = swiperRef.current.activeIndex

        trackAutoplayNavigation(
            { title: videoTitle, duration: videoDuration || 0 },
            activeIdx,
            gtmConfig?.listId,
            gtmConfig?.listName,
            gtmConfig?.category,
            gtmConfig?.category2
        )

        // Google Analytics tracking
        // if (typeof gtag !== 'undefined') {
        //     gtag('event', 'swipe', {
        //         event_category: 'Vertical-Video-Rail',
        //         event_label: label,
        //     })
        // }
    }, [])

    // Initialize Swiper when slides are ready
    useEffect(() => {
        if (!combinedSlides.length || !swiperLoaded || swiperInstance) return

        const initSwiper = () => {
            // Find initial index based on videoId prop
            let initialIndex = 0
            if (videoId) {
                const foundIndex = combinedSlides.findIndex((slide) => slide.type === 'video' && (slide.mediaid === videoId || slide.mediaid === parseInt(videoId) || slide.mediaid?.toString() === videoId?.toString()))
                if (foundIndex !== -1) {
                    initialIndex = foundIndex
                }
            }
            initialIndexRef.current = initialIndex
            const swiper = new window.Swiper('.mySwiper', {
                slidesPerView: 1,
                speed: 600,
                direction: 'vertical',
                cssMode: true,
                threshold: 50,
                allowTouchMove: true,
                touchRatio: 0.2,
                longSwipes: false,
                preventInteractionOnTransition: true,
                touchStartPreventDefault: false,
                resistance: true,
                resistanceRatio: 0.5,
                keyboard: {
                    enabled: true,
                },
                loop: false,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                initialSlide: 0,
                on: {
                    init: (swiper) => {
                        // If we need to go to a specific video (not the first one), slide there immediately
                        if (initialIndex > 0) {
                            swiper.slideTo(initialIndex, 0) // 0 = no animation, immediate

                            // Update refs and state to reflect the target index
                            currentSlideIndexRef.current = initialIndex
                            setCurrentSlideIndex(initialIndex)

                            // Update meta and load video for the target slide
                            updateMeta(initialIndex)
                            // loadVideoForSlide(initialIndex)

                            // Use initialIndex for background setup
                            const targetIndex = initialIndex
                            if (backgroundControllerRef.current) {
                                backgroundControllerRef.current.prepareAdjacentBackgrounds(targetIndex, swiper.slides)
                                backgroundControllerRef.current.ensureAdSlideAdjacentThumbnails(targetIndex, swiper.slides)
                            }
                            if (thumbnailPreloaderRef.current) {
                                thumbnailPreloaderRef.current.preloadAdjacentThumbnails(targetIndex, swiper.slides)
                            }
                        } else {
                            // Starting at first slide (index 0)
                            currentSlideIndexRef.current = 0
                            setCurrentSlideIndex(0)

                            updateMeta(0)
                            loadVideoForSlide(0)

                            if (backgroundControllerRef.current) {
                                backgroundControllerRef.current.prepareAdjacentBackgrounds(0, swiper.slides)
                                backgroundControllerRef.current.ensureAdSlideAdjacentThumbnails(0, swiper.slides)
                            }
                            if (thumbnailPreloaderRef.current) {
                                thumbnailPreloaderRef.current.preloadAdjacentThumbnails(0, swiper.slides)
                            }
                        }



                        // Setup like/dislike for initial slide
                        setupActiveSliceLikeDislike(swiper)
                    },
                    touchMove: (swiper, event) => {
                        try {
                            // Keep background visible during active swipe gesture
                            if (backgroundControllerRef.current) {
                                // active slide
                                const activeSlide = swiper.slides[swiper.activeIndex]

                                // Begin animation tracking for incomplete swipes
                                backgroundControllerRef.current.beginAnimationTracking()
                                backgroundControllerRef.current.handleSwipeStart(activeSlide, swiper.slides, swiper.activeIndex)
                            }
                        } catch (error) {
                            console.error('Error in touchMove handler:', error)
                        }
                    },
                    touchEnd: (swiper, event) => {
                        try {
                            if (backgroundControllerRef.current) {
                                // For incomplete swipes (swipes that don't result in slide change)
                                const activeSlide = swiper.slides[swiper.activeIndex]

                                // Only hide if we're not in the middle of a slide transition
                                // The transitionEnd event will handle hiding for actual slide changes
                                setTimeout(() => {
                                    // Check if we're still on the same slide after a brief delay
                                    if (swiper.slides[swiper.activeIndex] === activeSlide) {
                                        backgroundControllerRef.current.handleTouchEndSlideChange(activeSlide)
                                    }
                                }, 30)
                            }
                        } catch (error) {
                            console.error('Error in touchEnd handler:', error)
                        }
                    },
                    transitionStart: (swiper) => {
                        try {
                            if (backgroundControllerRef.current) {
                                backgroundControllerRef.current.beginAnimationTracking()
                            }
                        } catch (error) {
                            console.error('Error in transitionStart handler:', error)
                        }
                    },
                    transitionEnd: (swiper) => {
                        try {
                            if (backgroundControllerRef.current) {
                                backgroundControllerRef.current.endAnimationTracking()

                                // Only hide current slide thumbnail if video is loaded and visible
                                const currentSlide = swiper.slides[swiper.activeIndex]
                                if (currentSlide && !currentSlide.classList.contains('ad-slide')) {
                                    setTimeout(() => {
                                        const videoPlayer = document.getElementById('bitmovin-player')
                                        // Only hide if video player is visible (not on ad slide)
                                        if (videoPlayer && videoPlayer.style.display !== 'none') {
                                            backgroundControllerRef.current.forceHideBackground(currentSlide)
                                        }
                                    }, 30)
                                }
                            }
                        } catch (error) {
                            console.error('Error in transitionEnd handler:', error)
                        }
                    },
                    slideChangeTransitionStart: (swiper) => {
                        try {
                            if (backgroundControllerRef.current) {
                                backgroundControllerRef.current.beginAnimationTracking()
                            }
                        } catch (error) {
                            console.error('Error in slideChangeTransitionStart handler:', error)
                        }
                    },
                    slideChangeTransitionEnd: (swiper) => {
                        try {
                            if (backgroundControllerRef.current) {
                                backgroundControllerRef.current.endAnimationTracking()

                                // Final cleanup - only hide current slide thumbnail if video is ready
                                const currentSlide = swiper.slides[swiper.activeIndex]
                                if (currentSlide && !currentSlide.classList.contains('ad-slide')) {
                                    setTimeout(() => {
                                        const videoPlayer = document.getElementById('bitmovin-player')
                                        // Only hide if video player is visible and loaded
                                        if (videoPlayer && videoPlayer.style.display !== 'none' && playerRef.current && playerRef.current.isReady && playerRef.current.isReady()) {
                                            backgroundControllerRef.current.forceHideBackground(currentSlide)
                                        }
                                    }, 30)
                                }
                            }

                            // Setup like/dislike after transition is completely finished
                            setupActiveSliceLikeDislike(swiper)
                        } catch (error) {
                            console.error('Error in slideChangeTransitionEnd handler:', error)
                        }
                    },
                    slideChange: (swiper) => {
                        // Update current slide index for UI state management
                        currentSlideIndexRef.current = swiper.activeIndex
                        setCurrentSlideIndex(swiper.activeIndex)

                        const currentSlide = swiper.slides[swiper.activeIndex]
                        const previousSlide = swiper.slides[swiper.previousIndex]
                        const slideId = currentSlide.getAttribute('data-video-id')

                        // Handle background transitions for slide change
                        if (backgroundControllerRef.current) {
                            backgroundControllerRef.current.handleSlideChange(currentSlide, previousSlide, swiper.slides)
                        }

                        pauseVideo(swiper)
                        updateMeta(swiper.activeIndex)
                        loadVideoForSlide(swiper.activeIndex)

                        // Preload thumbnails for adjacent slides
                        if (thumbnailPreloaderRef.current) {
                            thumbnailPreloaderRef.current.preloadAdjacentThumbnails(swiper.activeIndex, swiper.slides)
                            thumbnailPreloaderRef.current.clearOldPreloads(swiper.activeIndex, swiper.slides)
                        }

                        if (backgroundControllerRef.current) {
                            backgroundControllerRef.current.prepareAdjacentBackgrounds(swiper.activeIndex, swiper.slides)

                            // Ensure adjacent thumbnails are set when on ad slide
                            backgroundControllerRef.current.ensureAdSlideAdjacentThumbnails(swiper.activeIndex, swiper.slides)
                        }

                        // Setup like/dislike for new slide (with small delay to ensure DOM is ready)
                        setTimeout(() => {
                            setupActiveSliceLikeDislike(swiper)
                        }, 100)

                        trackSwipe()
                    },
                },
            })

            setSwiperInstance(swiper)
            swiperRef.current = swiper
        }

        initSwiper()
    }, [combinedSlides, swiperLoaded, swiperInstance, videoId, loadVideoForSlide, updateMeta, pauseVideo, setupActiveSliceLikeDislike, trackSwipe])

    // Handle video player click
    const handlePlayerClick = useCallback(
        (e) => {
            // Don't trigger if clicking on action buttons
            if (e.target.closest('.actions, .back-btn')) return

            const player = playerRef.current
            if (!player || isPlayingAd) return

            try {
                const playerContainer = document.getElementById('bitmovin-player')
                const uiContainer = playerContainer?.querySelector('.bmpui-ui-uicontainer')

                if (uiContainer?.classList.contains('bmpui-controls-shown')) {
                    if (player.isPlaying && player.isPlaying()) {
                        player.pause && player.pause()
                    } else if (player.hasEnded && player.hasEnded()) {
                        player.seek && player.seek(0)
                        player.play && player.play()
                    } else {
                        player.play && player.play()
                    }
                } else {
                    // Simulate tap to show controls
                    const touchEvent = new TouchEvent('touchstart', { bubbles: true })
                    uiContainer?.dispatchEvent(touchEvent)
                    setTimeout(() => {
                        const touchEndEvent = new TouchEvent('touchend', { bubbles: true })
                        uiContainer?.dispatchEvent(touchEndEvent)
                    }, 50)
                }
            } catch (error) {
                console.error('Error in player click handler:', error)
            }
        },
        [isPlayingAd]
    )

    // Toggle global mute/unmute
    const toggleAudio = useCallback(() => {
        globalMuteState.current = !globalMuteState.current

        const player = playerRef.current
        if (!player) {
            forceUpdate({})
            return
        }

        try {
            if (isPlayingAd) {
                // Handle ad audio
                if (!globalMuteState.current) {
                    if (player.ads && player.ads.unmute) {
                        player.ads.unmute()
                    }
                    player.unmute && player.unmute()
                } else {
                    if (player.ads && player.ads.mute) {
                        player.ads.mute()
                    }
                    player.mute && player.mute()
                }
            } else {
                // Handle video audio
                if (!globalMuteState.current) {
                    player.unmute && player.unmute()
                } else {
                    player.mute && player.mute()
                }
            }
        } catch (error) {
            console.error('Error toggling audio:', error)
        }

        // Force re-render to update UI
        forceUpdate({})
    }, [isPlayingAd, forceUpdate])

    // Handle back navigation
    // const handleGoBack = useCallback(() => {
    //     if (document.referrer) {
    //         window.history.back()
    //     } else {
    //         router.push('/')
    //     }
    // }, [router])

    const handleGoBack = useCallback(() => {
        if (onClose) onClose()
    }, [onClose])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (backgroundControllerRef.current) {
                backgroundControllerRef.current.cleanup()
            }
            if (thumbnailPreloaderRef.current) {
                thumbnailPreloaderRef.current.cleanup()
            }
        }
    }, [])

    // Handle visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && playerRef.current) {
                if (playerRef.current.isPlaying && playerRef.current.isPlaying()) {
                    playerRef.current.pause()
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [])

    if (!isClient) return null

    return (
        <>
            <Helmet>
                <link rel='stylesheet' href='https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css' />
                <link rel='stylesheet' href='https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css' />
                {/* <link rel='stylesheet' href='https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css' /> */}
                <meta name='viewport' content='width=device-width, initial-scale=1.0' />

                {/* Google Ad Manager (GPT) Scripts */}
                {/* <script async src='https://securepubads.g.doubleclick.net/tag/js/gpt.js'></script>
                <script
                    dangerouslySetInnerHTML={{
                        __html: `
                            window.googletag = window.googletag || {cmd: []};
                            googletag.cmd.push(function() {
                                googletag.pubads().collapseEmptyDivs();
                                googletag.enableServices();
                            });

                            function loadAd(adId) {
                                window.definedAdSlots = window.definedAdSlots || new Set();
                                googletag.cmd.push(function() {
                                    if (!document.getElementById(adId) || document.getElementById(adId).hasAttribute('data-loaded')) {
                                      return;
                                    }
                                    if (!window.definedAdSlots.has(adId)) {
                                        googletag.defineSlot('/1009103/BUTV3_VP_Banner', [
                                          [300, 600],
                                          [320, 480]
                                        ], adId).addService(googletag.pubads());
                                        window.definedAdSlots.add(adId);
                                    }
                                    document.getElementById(adId).setAttribute('data-loaded', 'true');
                                    googletag.display(adId);
                                    googletag.pubads().refresh([
                                      googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === adId)
                                    ]);
                                });
                            }
                        `,
                    }}
                /> */}
            </Helmet>

            <div className='vertical-video-page-mobile'>
                {/* Global Video Player */}
                <div className='video-wrapper'>
                    <div className='video-container'>
                        <div id='bitmovin-player'></div>
                    </div>
                </div>

                {/* Swiper Container */}
                <div id='swiper' className='swiper mySwiper' onClick={handlePlayerClick}>
                    <div className='swiper-wrapper' id='swiper-wrapper'>
                        {combinedSlides.map((slide, index) =>
                            slide.type === 'video' ? (
                                <div key={`video-${slide.mediaid}-${index}`} className='swiper-slide' data-id={`bitmovin-${slide.index}`} data-video-id={slide.mediaid} data-title={slide.title} data-video-file={slide.hlsSource} data-poster={slide.poster} data-duration={slide.duration}>
                                    <button className='back-btn' id='goBack' onClick={handleGoBack}>
                                        <i className='fa-solid fa-arrow-left'></i>
                                    </button>

                                    <div className='actions'>
                                        {socialPanelOpenId === slide.mediaid ? (
                                        <>
                                            <button
                                                className='a-btn share-btn'
                                                aria-label='close social panel'
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    setSocialPanelOpenId(null)
                                                }}>
                                                <span className='icon'>
                                                    <i className='fa-solid fa-xmark'></i>
                                                </span>
                                            </button>
                                            {slide.YOUTUBE && (
                                                <a href={slide.YOUTUBE} target='_blank' rel='noopener noreferrer' className='a-btn social-follow-btn'>
                                                   <img src="/images/yt-icon.png" alt="YouTube" width="43" height="43" />
                                                </a>
                                            )}
                                            {slide.FACEBOOK && (
                                                <a href={slide.FACEBOOK} target='_blank' rel='noopener noreferrer' className='a-btn social-follow-btn'>
                                                    <img src="/images/fb-icon.png" alt="Facebook" width="43" height="43" />
                                                </a>
                                            )}
                                            {slide.TIKTOK && (
                                                <a href={slide.TIKTOK} target='_blank' rel='noopener noreferrer' className='a-btn social-follow-btn'>
                                                    <img src="/images/tiktok-icon.png" alt="TikTok" width="43" height="43" />
                                                </a>
                                            )}
                                            {slide.INSTAGRAM && (
                                                <a href={slide.INSTAGRAM} target='_blank' rel='noopener noreferrer' className='a-btn social-follow-btn'>
                                                    <img src="/images/insta-icon.png" alt="Instagram" width="43" height="43" />
                                                </a>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <label
                                                className='a-btn like-btn'
                                                data-video={slide.mediaid}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    handleLikeDislike('like', slide.mediaid)
                                                }}>
                                                <input type='radio' name={slide.mediaid} value='like' />
                                                <span className='icon like-icon'><i className='fa-solid fa-thumbs-up'></i></span>
                                                <span className='like-count'>{likeDislikeData[slide.mediaid]?.likes || 0}</span>
                                            </label>
                                            <label
                                                className='a-btn dislike-btn'
                                                data-video={slide.mediaid}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    handleLikeDislike('dislike', slide.mediaid)
                                                }}>
                                                <input type='radio' name={slide.mediaid} value='dislike' />
                                                <span className='icon dislike-icon'><i className='fa-solid fa-thumbs-down'></i></span>
                                                <span className='dislike-count'>{likeDislikeData[slide.mediaid]?.dislikes || 0}</span>
                                            </label>
                                            <button
                                                className='a-btn share-btn'
                                                aria-label='open social sharing menu'
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    handleShare()
                                                }}>
                                                <span className='icon share-icon'><i className='fa-solid fa-share'></i></span>
                                            </button>
                                            {(slide.YOUTUBE || slide.FACEBOOK || slide.TIKTOK || slide.INSTAGRAM) && (
                                                <button
                                                    className='dots-btn'
                                                    aria-label='show social links'
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        setSocialPanelOpenId(slide.mediaid)
                                                    }}>
                                                    <span className='ellipsis-icon'><i className='fa-solid fa-ellipsis'></i></span>
                                                </button>
                                            )}
                                        </>
                                    )}

                                        {/* <label
                                            className='a-btn like-btn'
                                            data-video={slide.mediaid}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                handleLikeDislike('like', slide.mediaid)
                                            }}>
                                            <input type='radio' name={slide.mediaid} value='like' />
                                            <span className='icon like-icon'>
                                                <i className='fa-solid fa-thumbs-up'></i>
                                            </span>
                                            <span className='like-count'>{likeDislikeData[slide.mediaid]?.likes || 0}</span>
                                        </label>
                                        <label
                                            className='a-btn dislike-btn'
                                            data-video={slide.mediaid}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                handleLikeDislike('dislike', slide.mediaid)
                                            }}>
                                            <input type='radio' name={slide.mediaid} value='dislike' />
                                            <span className='icon dislike-icon'>
                                                <i className='fa-solid fa-thumbs-down'></i>
                                            </span>
                                            <span className='dislike-count'>{likeDislikeData[slide.mediaid]?.dislikes || 0}</span>
                                        </label>
                                        <button
                                            className='a-btn share-btn'
                                            aria-label='open social sharing menu'
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                handleShare()
                                            }}>
                                            <span className='icon share-icon'>
                                                <i className='fa-solid fa-share'></i>
                                            </span>
                                        </button> */}
                                    </div>
                                    <p className='swipe-up'>Leret untuk lihat lebih</p>
                                </div>
                            ) : (
                                <div key={`ad-${index}`} className={`swiper-slide quick-container ad-slide`}>
                                    <div className='video-wrapper'>
                                        <div className='ad-container'>
                                            <p className='text-white'>IKLAN</p>
                                            <div id={slide.adId.toString()} style={{ minWidth: '300px', minHeight: '250px' }}></div>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Global Unmute Button */}
                <button
                    className='unmute-btn'
                    onClick={toggleAudio}
                    style={{
                        position: 'fixed',
                        bottom: '15px',
                        right: isPlayingAd ? '' : '15px',
                        left: isPlayingAd ? '15px' : '',
                        display: combinedSlides[currentSlideIndex]?.type === 'ad' ? 'none' : 'block',
                    }}>
                    <i className={`fa-solid ${!globalMuteState.current ? 'fa-volume-high' : 'fa-volume-xmark'}`}></i>
                </button>

                {/* Share Modal */}
                {shareModalOpen && (
                    <div className='share-modal'>
                        <div className='modal-dialog'>
                            <div className='modal-content'>
                                <div className='modal-header'>
                                    <h1 className='modal-title'>Kongsi Sekarang</h1>
                                    <button type='button' className='modal-close' onClick={() => setShareModalOpen(false)}>
                                        <i className='fa-solid fa-xmark'></i>
                                    </button>
                                </div>
                                <div className='modal-body'>
                                    <div className='share-icons'>
                                        <button className='share-icon share-fb' onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}>
                                            <i className='fab fa-facebook'></i>
                                        </button>
                                        <button className='share-icon share-tw' onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=Check this out!`, '_blank')}>
                                            <i className='fa-brands fa-square-x-twitter'></i>
                                        </button>
                                        <button className='share-icon share-wa' onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`, '_blank')}>
                                            <i className='fab fa-whatsapp'></i>
                                        </button>
                                    </div>
                                    <div className='share-copy'>
                                        <i className='fas fa-link'></i>
                                        <input type='text' value={shareUrl} readOnly />
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(shareUrl)
                                            }}>
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default MobileVerticalVideoPlayer
